import React, { useCallback, useEffect, useState } from 'react'
import type { CSSProperties } from 'react'
import { Link, useParams } from 'react-router-dom'
import { apiFetch, NetworkError } from '../driver/sync/api'
import { compressPhoto } from '../driver/sync/photo'
import { badge, button, card, color, container, h1, muted, page, TAP } from '../driver/ui'
import {
  WAITING_FOR_PERSON, discardWrite, drainWalk, enqueue, getWalkMeta, listWrites, loadSnapshot,
  onWalkChange, retryWrite, saveSnapshot, setWalkMeta, type WalkWrite,
} from './walkQueue'
import type { FeedProductOption, WalkState } from './types'

/**
 * One customer's property walk. Camera first, one location at a time: gate,
 * latch, storage, feeder, waterer, grit, shell, dog. Then the animals, the bins
 * and how full they are, the delivery day and tier, and the card link.
 *
 * Every entry is saved on the phone before anything else happens and uploads in
 * the background (walkQueue.ts), so the walk works with no signal. The one thing
 * that needs signal is sending the card link - staff need to know it went.
 */

const LOCATIONS: { kind: string; title: string; fields: { name: string; label: string }[] }[] = [
  { kind: 'gate', title: 'Gate', fields: [{ name: 'gate_location', label: 'Where is the gate?' }, { name: 'gate_code', label: 'Gate code' }] },
  { kind: 'latch', title: 'Latch', fields: [{ name: 'latch_notes', label: 'How the latch works' }] },
  { kind: 'storage', title: 'Feed storage', fields: [{ name: 'feed_storage_location', label: 'Where the bags go' }] },
  { kind: 'feeder', title: 'Feeder', fields: [{ name: 'feeder_location', label: 'Where the feeder is' }] },
  { kind: 'waterer', title: 'Waterer', fields: [{ name: 'waterer_location', label: 'Where the waterer is' }] },
  { kind: 'grit', title: 'Grit', fields: [{ name: 'grit_location', label: 'Where the grit is' }] },
  { kind: 'shell', title: 'Oyster shell', fields: [{ name: 'shell_location', label: 'Where the oyster shell is' }] },
  { kind: 'dog', title: 'Dog', fields: [{ name: 'dog_notes', label: 'About the dog' }] },
]
// Tuesday to Friday: Monday is pickup day (delivery/cycle.py).
const DAYS = ['tuesday', 'wednesday', 'thursday', 'friday']
const DROP_OPTIONS = [
  { value: 'front_door', label: 'Front door' },
  { value: 'back_door', label: 'Back door' },
  { value: 'garage', label: 'Garage' },
  { value: 'other', label: 'Other (photo)' },
]
const CATEGORIES = ['poultry', 'goat', 'sheep', 'swine', 'rabbit', 'horse', 'cattle', 'dog', 'cat', 'other']
const FORMS = ['pellet', 'mash', 'whole', 'kibble', 'other']
const TIER_LABEL: Record<string, string> = { full: 'Concierge', doorstep: 'Doorstep' }

const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1)
const inputStyle: CSSProperties = {
  width: '100%', boxSizing: 'border-box', minHeight: TAP, padding: '0 12px', fontSize: 18,
  border: `1px solid ${color.greyLight}`, borderRadius: 10, background: color.white,
}
const labelStyle: CSSProperties = { display: 'block', fontSize: 15, fontWeight: 700, margin: '10px 0 4px' }
const sectionTitle: CSSProperties = { fontSize: 22, fontWeight: 800, margin: '24px 0 8px' }

function PendingThumb({ blob }: { blob: Blob }) {
  const [url, setUrl] = useState('')
  useEffect(() => {
    const u = URL.createObjectURL(blob)
    setUrl(u)
    return () => URL.revokeObjectURL(u)
  }, [blob])
  return url ? <img src={url} alt="" style={{ width: 88, height: 88, objectFit: 'cover', borderRadius: 8, opacity: 0.7 }} /> : null
}

export default function WalkScreen({ offline }: { offline: boolean }) {
  const { id } = useParams()
  const subscriberId = Number(id)
  const [state, setState] = useState<WalkState | null>(null)
  const [writes, setWrites] = useState<WalkWrite[]>([])
  const [products, setProducts] = useState<FeedProductOption[]>([])
  const [loadError, setLoadError] = useState('')
  const [notice, setNotice] = useState('')
  const [drafts, setDrafts] = useState<Record<string, string>>({})
  const [levels, setLevels] = useState<Record<number, string>>({})
  const [animal, setAnimal] = useState({ category: 'poultry', animal_type: '', count: '1' })
  const [newBin, setNewBin] = useState({ name: '', feed_category: 'poultry', form_type: 'pellet', feed_product_id: '', current_lbs: '', animal_ids: [] as number[] })
  const [prefs, setPrefs] = useState<{ bin_ids: number[]; preferred_day: string; service_tier: string } | null>(null)
  const [sending, setSending] = useState(false)

  const refreshLocal = useCallback(async () => {
    const [snap, w] = await Promise.all([loadSnapshot(subscriberId), listWrites(subscriberId)])
    if (snap) setState(snap.state)
    setWrites(w)
  }, [subscriberId])

  useEffect(() => {
    void refreshLocal()
    return onWalkChange(() => {
      void refreshLocal()
    })
  }, [refreshLocal])

  useEffect(() => {
    let alive = true
    ;(async () => {
      try {
        const res = await apiFetch(`/delivery/walk/${subscriberId}/`)
        const body = await res.json().catch(() => null)
        if (!res.ok) {
          if (alive) setLoadError(body?.error || `Could not load this walk (${res.status}).`)
        } else {
          await saveSnapshot(body as WalkState)
        }
      } catch (err) {
        if (alive && !(err instanceof NetworkError)) setLoadError('Could not load this walk.')
      }
      try {
        const res = await apiFetch('/delivery/walk/feed-products/')
        if (res.ok) {
          const body = await res.json()
          await setWalkMeta('feedProducts', body.feed_products)
          if (alive) setProducts(body.feed_products)
        }
      } catch {
        const cached = await getWalkMeta<FeedProductOption[]>('feedProducts')
        if (alive && cached) setProducts(cached)
      }
      void drainWalk()
    })()
    const timer = window.setInterval(() => {
      void drainWalk()
    }, 20000)
    const online = () => {
      void drainWalk()
    }
    window.addEventListener('online', online)
    return () => {
      alive = false
      window.clearInterval(timer)
      window.removeEventListener('online', online)
    }
  }, [subscriberId])

  // Seed the delivery choices from the server once, then leave them to the manager.
  useEffect(() => {
    if (state && prefs === null) {
      setPrefs({
        bin_ids: state.bins.filter((b) => b.selected).map((b) => b.id),
        preferred_day: state.subscriber.preferred_day || '',
        service_tier: state.subscriber.service_tier || 'full',
      })
    }
  }, [state, prefs])

  const base = `/delivery/walk/${subscriberId}`
  const accessText = (name: string) => {
    if (drafts[name] !== undefined) return drafts[name]
    const v = state?.access?.[name]
    return typeof v === 'string' ? v : ''
  }

  const saveField = async (name: string, label: string) => {
    const value = drafts[name]
    if (value === undefined || !state) return
    const current = typeof state.access?.[name] === 'string' ? (state.access?.[name] as string) : ''
    if (value.trim() === current.trim()) return
    await enqueue({ subscriberId, path: `${base}/access/`, method: 'PATCH', body: { [name]: value }, label })
    await saveSnapshot({ ...state, access: { ...(state.access || {}), [name]: value } })
  }

  const toggleDog = async (name: 'dog_present' | 'dog_loose') => {
    if (!state) return
    const next = !state.access?.[name]
    await enqueue({ subscriberId, path: `${base}/access/`, method: 'PATCH', body: { [name]: next },
      label: `${name === 'dog_present' ? 'Dog on property' : 'Dog runs loose'}: ${next ? 'yes' : 'no'}` })
    await saveSnapshot({ ...state, access: { ...(state.access || {}), [name]: next } })
  }

  // Where the bags go: one of four. Same offline queue as every other access fact.
  const setDrop = async (value: string) => {
    if (!state) return
    await enqueue({ subscriberId, path: `${base}/access/`, method: 'PATCH', body: { drop_location: value },
      label: `Bags go: ${DROP_OPTIONS.find((o) => o.value === value)?.label ?? value}` })
    await saveSnapshot({ ...state, access: { ...(state.access || {}), drop_location: value } })
  }

  const takePhoto = async (kind: string, title: string, file: File | undefined) => {
    if (!file) return
    let blob: Blob = file
    try {
      blob = (await compressPhoto(file)).blob
    } catch {
      // A format this browser cannot decode goes up as taken; the server checks it.
    }
    await enqueue({ subscriberId, path: `${base}/photos/`, method: 'POST', photo: { blob, kind, caption: '' }, label: `${title} photo` })
  }

  const addAnimal = async () => {
    if (!animal.animal_type.trim()) {
      setNotice('Say what the animals are, e.g. Laying hens.')
      return
    }
    await enqueue({ subscriberId, path: `${base}/animals/`, method: 'POST',
      body: { category: animal.category, animal_type: animal.animal_type.trim(), count: Number(animal.count) || 1 },
      label: `Animals: ${animal.animal_type.trim()} × ${Number(animal.count) || 1}` })
    setAnimal({ category: animal.category, animal_type: '', count: '1' })
    setNotice('')
  }

  const addBin = async () => {
    if (!newBin.name.trim()) {
      setNotice('Give the bin a name, e.g. Layer bin.')
      return
    }
    const body: Record<string, unknown> = {
      name: newBin.name.trim(), feed_category: newBin.feed_category, form_type: newBin.form_type,
      animal_ids: newBin.animal_ids,
    }
    if (newBin.feed_product_id) body.feed_product_id = Number(newBin.feed_product_id)
    if (newBin.current_lbs.trim()) body.current_lbs = newBin.current_lbs.trim()
    await enqueue({ subscriberId, path: `${base}/bins/`, method: 'POST', body, label: `Bin: ${newBin.name.trim()}` })
    setNewBin({ name: '', feed_category: newBin.feed_category, form_type: newBin.form_type, feed_product_id: '', current_lbs: '', animal_ids: [] })
    setNotice('')
  }

  const saveLevel = async (binId: number, name: string) => {
    const value = (levels[binId] || '').trim()
    if (!value) return
    await enqueue({ subscriberId, path: `${base}/bins/${binId}/`, method: 'PATCH', body: { current_lbs: value }, label: `${name}: ${value} lb` })
    setLevels({ ...levels, [binId]: '' })
  }

  const setBinProduct = async (binId: number, name: string, productId: string) => {
    if (!productId) return
    const product = products.find((p) => p.id === Number(productId))
    await enqueue({ subscriberId, path: `${base}/bins/${binId}/`, method: 'PATCH', body: { feed_product_id: Number(productId) },
      label: `${name}: ${product ? product.name : 'feed set'}` })
  }

  const savePrefs = async () => {
    if (!prefs) return
    if (!prefs.preferred_day) {
      setNotice('Pick a delivery day.')
      return
    }
    await enqueue({ subscriberId, path: `${base}/preferences/`, method: 'PATCH', body: prefs,
      label: `Delivery: ${prefs.bin_ids.length} bin(s), ${cap(prefs.preferred_day)}, ${TIER_LABEL[prefs.service_tier] || prefs.service_tier}` })
    setNotice('')
  }

  const sendCardLink = async () => {
    setSending(true)
    setNotice('')
    try {
      const res = await apiFetch(`${base}/card-link/`, { method: 'POST' })
      const body = await res.json().catch(() => null)
      if (body && body.subscriber) await saveSnapshot(body as WalkState)
      setNotice(res.ok ? `Card link sent to ${body?.card_link?.sent_to}. Ask them to open it on their phone.`
        : body?.error || `The link was not sent (${res.status}).`)
    } catch (err) {
      setNotice(err instanceof NetworkError ? 'Sending the card link needs signal. Everything else is saved on this phone.'
        : 'The link was not sent.')
    } finally {
      setSending(false)
    }
  }

  const finish = async () => {
    await enqueue({ subscriberId, path: `${base}/finish/`, method: 'POST', body: {}, label: 'Walk finished' })
    setNotice('Walk finished. It uploads as soon as there is signal.')
  }

  if (!state) {
    return (
      <div style={page}>
        <div style={container}>
          <Link to="/delivery/walk" style={{ color: color.grey }}>← All customers</Link>
          <div style={h1}>{loadError ? 'Walk not available' : 'Opening walk…'}</div>
          <div style={muted}>{loadError || (offline ? 'No signal, and this walk was never opened on this phone.' : '')}</div>
        </div>
      </div>
    )
  }

  const waiting = writes.filter((w) => w.state !== 'synced')
  const sub = state.subscriber
  const photosFor = (kind: string) => state.photos.filter((p) => p.kind === kind)
  const queuedPhotosFor = (kind: string) => waiting.filter((w) => w.photo && w.photo.kind === kind)

  return (
    <div style={page}>
      <div style={container}>
        <Link to="/delivery/walk" style={{ color: color.grey, fontSize: 16 }}>← All customers</Link>
        <div style={h1}>{sub.name}</div>
        <div style={muted}>{sub.service_address}, {sub.city} {sub.postal_code}</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, margin: '10px 0' }}>
          <span style={badge(state.surveyed_at ? color.green : color.amber)}>{state.surveyed_at ? 'Walked' : 'Not walked yet'}</span>
          <span style={badge(state.has_card ? color.green : color.grey)}>{state.has_card ? 'Card on file' : 'No card yet'}</span>
          <span style={badge(waiting.length ? color.amber : color.green)}>
            {waiting.length ? `${waiting.length} waiting to upload` : 'All uploaded'}
          </span>
        </div>

        {notice && <div style={{ ...card, background: '#eff6ff', borderColor: color.blue }}>{notice}</div>}

        {waiting.length > 0 && (
          <div style={card}>
            <div style={{ fontWeight: 800, marginBottom: 6 }}>Saved on this phone</div>
            {waiting.map((w) => (
              <div key={w.id} style={{ padding: '6px 0', borderTop: `1px solid ${color.greyLight}` }}>
                <div style={{ fontWeight: 600 }}>{w.label}</div>
                {w.lastError && <div style={{ fontSize: 14, color: w.nextAttemptAt === WAITING_FOR_PERSON ? color.red : color.grey }}>{w.lastError}</div>}
                {w.nextAttemptAt === WAITING_FOR_PERSON && (
                  <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                    <button style={button(color.blue)} onClick={() => retryWrite(w.id)}>Try again</button>
                    <button style={button(color.white, color.ink)} onClick={() => discardWrite(w.id)}>Discard</button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        <div style={sectionTitle}>Walk the property</div>
        <div style={card}>
          <div style={{ fontSize: 20, fontWeight: 800 }}>Where do the bags go?</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
            {DROP_OPTIONS.map((o) => {
              const on = state.access?.drop_location === o.value
              return (
                <button key={o.value} style={button(on ? color.green : color.white, on ? color.white : color.ink)}
                  onClick={() => setDrop(o.value)}>{o.label}{on ? ' ✓' : ''}</button>
              )
            })}
          </div>
          {state.access?.drop_location === 'garage' && (
            <div>
              <label style={labelStyle} htmlFor="garage_code">Garage code</label>
              <input id="garage_code" style={inputStyle} value={accessText('garage_code')}
                onChange={(e) => setDrafts({ ...drafts, garage_code: e.target.value })}
                onBlur={() => saveField('garage_code', 'Garage code')} />
            </div>
          )}
          {state.access?.drop_location === 'other' && (
            <label style={{ ...button(color.blue), display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: 8 }}>
              Take a photo of the drop spot
              <input type="file" accept="image/*" capture="environment" style={{ display: 'none' }}
                onChange={(e) => { void takePhoto('drop', 'Drop spot', e.target.files?.[0]); e.target.value = '' }} />
            </label>
          )}
        </div>
        {LOCATIONS.map((loc) => (
          <div key={loc.kind} style={card}>
            <div style={{ fontSize: 20, fontWeight: 800 }}>{loc.title}</div>
            <label style={{ ...button(color.blue), display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: 8 }}>
              Take a photo of the {loc.title.toLowerCase()}
              <input type="file" accept="image/*" capture="environment" style={{ display: 'none' }}
                onChange={(e) => {
                  void takePhoto(loc.kind, loc.title, e.target.files?.[0])
                  e.target.value = ''
                }} />
            </label>
            {(photosFor(loc.kind).length > 0 || queuedPhotosFor(loc.kind).length > 0) && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                {photosFor(loc.kind).map((p) => (
                  <img key={p.id} src={p.url} alt={loc.title} style={{ width: 88, height: 88, objectFit: 'cover', borderRadius: 8 }} />
                ))}
                {queuedPhotosFor(loc.kind).map((w) => w.photo && <PendingThumb key={w.id} blob={w.photo.blob} />)}
              </div>
            )}
            {loc.kind === 'dog' && (
              <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                <button style={button(state.access?.dog_present ? color.green : color.white, state.access?.dog_present ? color.white : color.ink)}
                  onClick={() => toggleDog('dog_present')}>
                  {state.access?.dog_present ? 'Dog on property ✓' : 'Dog on property?'}
                </button>
                <button style={button(state.access?.dog_loose ? color.amber : color.white, state.access?.dog_loose ? color.white : color.ink)}
                  onClick={() => toggleDog('dog_loose')}>
                  {state.access?.dog_loose ? 'Runs loose ✓' : 'Runs loose?'}
                </button>
              </div>
            )}
            {loc.fields.map((f) => (
              <div key={f.name}>
                <label style={labelStyle} htmlFor={f.name}>{f.label}</label>
                <input id={f.name} style={inputStyle} value={accessText(f.name)}
                  onChange={(e) => setDrafts({ ...drafts, [f.name]: e.target.value })}
                  onBlur={() => saveField(f.name, `${loc.title}: ${f.label.toLowerCase()}`)} />
              </div>
            ))}
          </div>
        ))}
        <div style={card}>
          <label style={labelStyle} htmlFor="notes">Anything else the driver should know</label>
          <textarea id="notes" style={{ ...inputStyle, minHeight: 96, padding: 12 }} value={accessText('notes')}
            onChange={(e) => setDrafts({ ...drafts, notes: e.target.value })}
            onBlur={() => saveField('notes', 'Notes for the driver')} />
        </div>

        <div style={sectionTitle}>Animals</div>
        <div style={card}>
          {state.animals.length === 0 && <div style={muted}>No animals yet.</div>}
          {state.animals.map((a) => (
            <div key={a.id} style={{ padding: '4px 0', fontSize: 17 }}>{a.animal_type} × {a.count} <span style={muted}>({a.category})</span></div>
          ))}
          <label style={labelStyle}>Kind</label>
          <select style={inputStyle} value={animal.category} onChange={(e) => setAnimal({ ...animal, category: e.target.value })}>
            {CATEGORIES.map((c) => <option key={c} value={c}>{cap(c)}</option>)}
          </select>
          <label style={labelStyle}>What they are</label>
          <input style={inputStyle} placeholder="e.g. Laying hens" value={animal.animal_type}
            onChange={(e) => setAnimal({ ...animal, animal_type: e.target.value })} />
          <label style={labelStyle}>How many</label>
          <input style={inputStyle} inputMode="numeric" value={animal.count} onChange={(e) => setAnimal({ ...animal, count: e.target.value })} />
          <button style={{ ...button(color.green), marginTop: 10, width: '100%' }} onClick={addAnimal}>Add animals</button>
        </div>

        <div style={sectionTitle}>Feed bins</div>
        {state.bins.map((b) => (
          <div key={b.id} style={card}>
            <div style={{ fontSize: 19, fontWeight: 800 }}>{b.name}</div>
            <div style={muted}>{b.current_lbs} lb now · {b.feed_product ? b.feed_product.name : 'feed not set'}</div>
            {!b.deliverable && b.reason && <div style={{ fontSize: 14, color: color.amber, marginTop: 4 }}>{b.reason}</div>}
            <label style={labelStyle}>Feed in this bin</label>
            <select style={inputStyle} value="" onChange={(e) => setBinProduct(b.id, b.name, e.target.value)}>
              <option value="">{b.feed_product ? `Change from ${b.feed_product.name}` : 'Choose the Red Bridge feed'}</option>
              {products.map((p) => <option key={p.id} value={p.id}>{p.name} ({p.animal_category})</option>)}
            </select>
            <label style={labelStyle}>Measured level (lb)</label>
            <div style={{ display: 'flex', gap: 8 }}>
              <input style={inputStyle} inputMode="decimal" value={levels[b.id] || ''} placeholder={b.current_lbs}
                onChange={(e) => setLevels({ ...levels, [b.id]: e.target.value })} />
              <button style={button(color.green)} onClick={() => saveLevel(b.id, b.name)}>Save</button>
            </div>
          </div>
        ))}
        <div style={card}>
          <div style={{ fontWeight: 800 }}>Add a bin</div>
          <label style={labelStyle}>Name</label>
          <input style={inputStyle} placeholder="e.g. Layer bin" value={newBin.name} onChange={(e) => setNewBin({ ...newBin, name: e.target.value })} />
          <label style={labelStyle}>For</label>
          <select style={inputStyle} value={newBin.feed_category} onChange={(e) => setNewBin({ ...newBin, feed_category: e.target.value })}>
            {CATEGORIES.map((c) => <option key={c} value={c}>{cap(c)}</option>)}
          </select>
          <label style={labelStyle}>Feed</label>
          <select style={inputStyle} value={newBin.feed_product_id} onChange={(e) => setNewBin({ ...newBin, feed_product_id: e.target.value })}>
            <option value="">Choose the Red Bridge feed</option>
            {products.map((p) => <option key={p.id} value={p.id}>{p.name} ({p.animal_category})</option>)}
          </select>
          <label style={labelStyle}>Form</label>
          <select style={inputStyle} value={newBin.form_type} onChange={(e) => setNewBin({ ...newBin, form_type: e.target.value })}>
            {FORMS.map((f) => <option key={f} value={f}>{cap(f)}</option>)}
          </select>
          <label style={labelStyle}>Measured level (lb)</label>
          <input style={inputStyle} inputMode="decimal" value={newBin.current_lbs} onChange={(e) => setNewBin({ ...newBin, current_lbs: e.target.value })} />
          {state.animals.length > 0 && <label style={labelStyle}>Animals eating from it</label>}
          {state.animals.map((a) => {
            const on = newBin.animal_ids.includes(a.id)
            return (
              <button key={a.id} style={{ ...button(on ? color.green : color.white, on ? color.white : color.ink), margin: '0 8px 8px 0' }}
                onClick={() => setNewBin({ ...newBin, animal_ids: on ? newBin.animal_ids.filter((x) => x !== a.id) : [...newBin.animal_ids, a.id] })}>
                {a.animal_type}{on ? ' ✓' : ''}
              </button>
            )
          })}
          <button style={{ ...button(color.green), marginTop: 10, width: '100%' }} onClick={addBin}>Add bin</button>
        </div>

        <div style={sectionTitle}>Delivery</div>
        {prefs && (
          <div style={card}>
            <label style={labelStyle}>Bins to keep filled</label>
            {state.selection_note && <div style={{ ...muted, marginBottom: 8 }}>{state.selection_note}</div>}
            {state.bins.length === 0 && <div style={muted}>Add a bin first.</div>}
            {state.bins.map((b) => {
              const on = prefs.bin_ids.includes(b.id)
              return (
                <button key={b.id} disabled={!b.deliverable}
                  style={{ ...button(on ? color.green : color.white, on ? color.white : color.ink, !b.deliverable), margin: '0 8px 8px 0' }}
                  onClick={() => setPrefs({ ...prefs, bin_ids: on ? prefs.bin_ids.filter((x) => x !== b.id) : [...prefs.bin_ids, b.id] })}>
                  {b.name}{on ? ' ✓' : ''}
                </button>
              )
            })}
            <label style={labelStyle}>Preferred day</label>
            <select style={inputStyle} value={prefs.preferred_day} onChange={(e) => setPrefs({ ...prefs, preferred_day: e.target.value })}>
              <option value="">Choose a day</option>
              {DAYS.map((d) => <option key={d} value={d}>{cap(d)}</option>)}
            </select>
            <label style={labelStyle}>Service</label>
            <div style={{ display: 'flex', gap: 8 }}>
              {state.tier_options.map((t) => (
                <button key={t} style={{ ...button(prefs.service_tier === t ? color.green : color.white, prefs.service_tier === t ? color.white : color.ink), flex: 1 }}
                  onClick={() => setPrefs({ ...prefs, service_tier: t })}>
                  {TIER_LABEL[t] || t}
                </button>
              ))}
            </div>
            <button style={{ ...button(color.green), marginTop: 12, width: '100%' }} onClick={savePrefs}>Save delivery choices</button>
          </div>
        )}

        <div style={sectionTitle}>Card</div>
        <div style={card}>
          <div style={{ fontSize: 17, marginBottom: 8 }}>
            {state.has_card ? 'The customer has added a card.' : 'The customer adds their own card. We never see it.'}
          </div>
          {state.card_link && (
            <div style={{ ...muted, marginBottom: 8 }}>
              Last link sent to {state.card_link.sent_to}
              {state.card_link.used_at ? ' · opened' : state.card_link.revoked ? ' · replaced' : ` · expires ${new Date(state.card_link.expires_at).toLocaleString()}`}
            </div>
          )}
          {!state.has_card && (
            <button style={{ ...button(color.blue, color.white, sending), width: '100%' }} disabled={sending} onClick={sendCardLink}>
              {sending ? 'Sending…' : state.card_link ? 'Send a new card link' : 'Send card link to the customer'}
            </button>
          )}
        </div>

        <button style={{ ...button(color.greenDark), width: '100%', minHeight: 72, fontSize: 22, marginTop: 20 }} onClick={finish}>
          Finish walk
        </button>
      </div>
    </div>
  )
}
